import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent, subscribeCookieConsent } from '../../legal/cookieConsent';

const COUNTER_ID = Number(process.env.REACT_APP_YM_ID || 0) || 0;
const TAG_BASE = 'https://mc.yandex.ru/metrika/tag.js';

function getMetrikaInitOptions() {
  return {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    accurateTrackBounce: true,
    trackLinks: true,
  };
}

function ensureDataLayer() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
}

function loadMetrikaTag(counterId) {
  if (typeof window === 'undefined' || !counterId) return;

  const tagUrl = `${TAG_BASE}?id=${counterId}`;
  for (let i = 0; i < document.scripts.length; i += 1) {
    if (document.scripts[i].src === tagUrl) return;
  }

  window.ym =
    window.ym ||
    function ymStub() {
      (window.ym.a = window.ym.a || []).push(arguments);
    };
  window.ym.l = Date.now();

  const script = document.createElement('script');
  script.async = true;
  script.src = tagUrl;
  const first = document.getElementsByTagName('script')[0];
  first.parentNode.insertBefore(script, first);
}

function initMetrika(counterId) {
  if (!counterId || typeof window.ym !== 'function') return;
  ensureDataLayer();
  window.ym(counterId, 'init', {
    ...getMetrikaInitOptions(),
    referrer: document.referrer,
    url: window.location.href,
  });
}

export default function YandexMetrika() {
  const location = useLocation();
  const [allowed, setAllowed] = useState(() => hasAnalyticsConsent());
  const initializedRef = useRef(false);
  const prevUrlRef = useRef('');

  useEffect(() => subscribeCookieConsent((value) => setAllowed(value === 'all')), []);

  useEffect(() => {
    if (!COUNTER_ID || !allowed) return undefined;
    loadMetrikaTag(COUNTER_ID);
    initMetrika(COUNTER_ID);
    initializedRef.current = true;
    prevUrlRef.current = window.location.pathname + window.location.search;
    return undefined;
  }, [allowed]);

  useEffect(() => {
    if (!COUNTER_ID || !allowed || !initializedRef.current) return;
    if (typeof window.ym !== 'function') return;

    const url = location.pathname + location.search;
    if (url === prevUrlRef.current) return;

    const referer = prevUrlRef.current
      ? `${window.location.origin}${prevUrlRef.current}`
      : document.referrer;

    window.ym(COUNTER_ID, 'hit', url, { referer });
    prevUrlRef.current = url;
  }, [location.pathname, location.search, allowed]);

  if (!COUNTER_ID || !allowed) return null;

  return (
    <noscript>
      <div>
        <img
          src={`https://mc.yandex.ru/watch/${COUNTER_ID}`}
          style={{ position: 'absolute', left: '-9999px' }}
          alt=""
        />
      </div>
    </noscript>
  );
}
